import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Grid,
  Chip,
  CircularProgress,
  Paper,
  Slider,
  Stack
} from '@mui/material'
import {
  CheckCircle,
  AccessTime,
  Support,
  Security,
  Speed
} from '@mui/icons-material'
import { loadStripe } from '@stripe/stripe-js'
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js'
import { useAuth } from '@shared/auth/AuthContext'
import BillingService, { TrialStatus } from '@shared/services/BillingService'
import { apiClient } from '@shared/services/ApiClient'

const PRICE_PER_AGENT = 25 // £25 per agent per month
const FEATURES = [
  'Unlimited tickets',
  'Email and portal access',
  'Priority email support',
  'Advanced reporting & analytics',
  'Unlimited ticket history',
  'Teams integration',
  'Zapier integration',
  'API access',
  'Custom branding',
  'SLA management'
]

function UpgradePage() {
  const { } = useAuth()
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [agentCount, setAgentCount] = useState(5)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)

  useEffect(() => {
    loadTrialStatus()
    initializeStripe()
  }, [])

  const initializeStripe = async () => {
    try {
      // Fetch Stripe publishable key from API
      const response = await apiClient.get<{ publishableKey: string }>('/billing/stripe-config')

      if (!response.publishableKey) {
        setError('Stripe configuration is not available')
        return
      }

      setStripePromise(loadStripe(response.publishableKey))
    } catch (err) {
      console.error('Failed to initialize Stripe:', err)
      setError('Failed to initialize payment system')
    }
  }

  const loadTrialStatus = async () => {
    try {
      const data = await BillingService.getTrialStatus()
      setTrialStatus(data)
    } catch (err) {
      console.error('Error loading trial status:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchClientSecret = useCallback(async () => {
    if (!agentCount) return ''

    try {
      setProcessing(true)
      setError(null)

      // Create checkout session
      const checkoutSession = await apiClient.post<{
        sessionId: string
        clientSecret: string
      }>('/billing/checkout-session', {
        agentCount,
        successUrl: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href
      })

      if (!checkoutSession || !checkoutSession.clientSecret) {
        throw new Error('Failed to create checkout session')
      }

      return checkoutSession.clientSecret
    } catch (err: any) {
      console.error('Checkout error:', err)
      setError('An error occurred during checkout. Please try again.')
      throw err
    } finally {
      setProcessing(false)
    }
  }, [agentCount])

  const handleUpgrade = async () => {
    if (!stripePromise) {
      setError('Payment system is not ready. Please refresh the page.')
      return
    }

    setShowCheckout(true)
  }

  const handleAgentCountChange = (_event: Event, newValue: number | number[]) => {
    setAgentCount(newValue as number)
  }

  const monthlyTotal = agentCount * PRICE_PER_AGENT

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your account information...
        </Typography>
      </Container>
    )
  }

  const isTrialExpired = trialStatus?.accountStatus === 'expired_trial'
  const daysRemaining = trialStatus?.daysRemaining || 0

  // If checkout is shown, display only the embedded checkout
  if (showCheckout && stripePromise) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          onClick={() => setShowCheckout(false)}
          sx={{ mb: 2 }}
        >
          ← Back to pricing
        </Button>
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ fetchClientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          Upgrade to Pro
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Unlock the full power of Helpdesk Pro for your team
        </Typography>
      </Box>

      {/* Trial Status Alert */}
      {!trialStatus?.hasActiveSubscription && (
        <Alert
          severity={isTrialExpired ? 'error' : 'info'}
          icon={isTrialExpired ? undefined : <AccessTime />}
          sx={{ mb: 4 }}
        >
          {isTrialExpired ? (
            <>
              <Typography variant="body1" fontWeight="bold">
                Your trial has expired
              </Typography>
              <Typography variant="body2">
                Upgrade now to continue using Helpdesk Pro
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" fontWeight="bold">
                {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left in your trial
              </Typography>
              <Typography variant="body2">
                Upgrade now to ensure uninterrupted service
              </Typography>
            </>
          )}
        </Alert>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Pricing Card */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={3}
            sx={{
              height: '100%',
              border: '2px solid',
              borderColor: 'primary.main'
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 3 }}>
                <Chip
                  label="BEST VALUE"
                  color="primary"
                  size="small"
                  sx={{ mb: 2 }}
                />
                <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
                  Helpdesk Pro
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Perfect for teams of all sizes
                </Typography>
              </Box>

              {/* Agent Count Slider */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Number of agent seats
                </Typography>
                <Box sx={{ px: 2, py: 3 }}>
                  <Slider
                    value={agentCount}
                    onChange={handleAgentCountChange}
                    min={1}
                    max={50}
                    marks={[
                      { value: 1, label: '1' },
                      { value: 10, label: '10' },
                      { value: 25, label: '25' },
                      { value: 50, label: '50' }
                    ]}
                    valueLabelDisplay="on"
                    disabled={processing}
                  />
                </Box>
              </Box>

              {/* Pricing Display */}
              <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">
                    {agentCount} agent{agentCount !== 1 ? 's' : ''} × £{PRICE_PER_AGENT}/month
                  </Typography>
                </Stack>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 2 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="h5" fontWeight="bold">
                    Total
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h3" fontWeight="bold" color="primary">
                      £{monthlyTotal}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      per month
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* CTA Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleUpgrade}
                disabled={processing}
                sx={{ mb: 2, py: 1.5 }}
              >
                {processing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Upgrade Now'
                )}
              </Button>

              <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                Cancel anytime. No long-term contracts.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Features List */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                Everything you need
              </Typography>

              <Stack spacing={2}>
                {FEATURES.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: index % 2 === 0 ? 'action.hover' : 'transparent'
                    }}
                  >
                    <CheckCircle color="primary" />
                    <Typography variant="body1">{feature}</Typography>
                  </Box>
                ))}
              </Stack>

              {/* Trust Badges */}
              <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Security color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="caption" display="block">
                        Bank-level security
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Support color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="caption" display="block">
                        24/7 support
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Speed color="primary" sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="caption" display="block">
                        99.9% uptime
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default UpgradePage
